import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Stack,
  Paper,
  IconButton,
  Button,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { getClientStatusDisplay } from "../utils/crmHelpers";
import { CLIENT_STATUS_OPTIONS, CLIENT_TYPE_OPTIONS } from "../types/crmTypes";

const ClientList = ({
  clients = [],
  onSelect,
  onEdit,
  onDelete,
  filters = {},
  onFilterChange,
  loading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters?.search || "");

  const handleSearch = (e) => {
    e.preventDefault();
    if (onFilterChange) {
      onFilterChange({ search: searchTerm });
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    if (onFilterChange) {
      onFilterChange({ search: "", status: "all", type: "all" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No records found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <form onSubmit={handleSearch} style={{ flex: 1, minWidth: 220 }}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" size="small">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </form>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters?.status || "all"}
              onChange={(e) =>
                onFilterChange && onFilterChange({ status: e.target.value })
              }
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              {CLIENT_STATUS_OPTIONS.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters?.type || "all"}
              onChange={(e) =>
                onFilterChange && onFilterChange({ type: e.target.value })
              }
              label="Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              {CLIENT_TYPE_OPTIONS.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {clients.map((client) => {
          const statusDisplay = getClientStatusDisplay(client.status);

          return (
            <Grid item xs={12} sm={6} md={4} key={client.id}>
              <Card
                sx={{
                  height: "100%",
                  transition: "0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" noWrap>
                      {client.fullName}
                    </Typography>
                    <Chip
                      label={statusDisplay.label}
                      color={statusDisplay.color}
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    {client.phone || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {client.email || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {client.type || "-"} • {client.clientCode || "-"}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onSelect?.(client.id)}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onEdit?.(client.id)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => onDelete?.(client.id)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ClientList;
